import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import DataTable from "react-data-table-component";
import {
  Alert,
  Badge,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import { fetchBranches } from "../../../api/users";
import {
  closeFinancialPeriod,
  createFinancialPeriod,
  fetchFinancialPeriods,
  reopenFinancialPeriod,
} from "../../../api/accounting";
import { formatDate } from "../../../utils/format";
import { useConfirmDialog } from "../Common/ConfirmDialogContext";
import { DEFAULT_TABLE_CUSTOM_STYLES } from "../Common/credoraTableUtils";
import { formatApiError, runCrud, runLoad } from "../Common/credoraToast";
import { ACCOUNTING_SECTION } from "./accountingPlaceholders";
import AccountingIconActions from "./AccountingIconActions";
import { PERIOD_STATUS_META, todayIsoDate, useLatestRef } from "./accountingUtils";

const FinancialPeriodsPage = () => {
  const { confirm } = useConfirmDialog();
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "",
    period_start: todayIsoDate().slice(0, 7) + "-01",
    period_end: todayIsoDate(),
  });

  useEffect(() => {
    fetchBranches(true).then((rows) => {
      setBranches(rows || []);
      if (rows?.length) setBranchId(String(rows[0].id));
    });
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    return runLoad(() => fetchFinancialPeriods(branchId || undefined).then(setPeriods), {
      errorMessage: "Failed to load financial periods.",
    })
      .catch((err) => setError(formatApiError(err, "Failed to load financial periods.")))
      .finally(() => setLoading(false));
  }, [branchId]);

  useEffect(() => {
    load();
  }, [load]);

  const loadRef = useLatestRef(load);

  const togglePeriod = useCallback(
    (row, close) => {
      confirm({
        title: close ? `Close "${row.name}"?` : `Reopen "${row.name}"?`,
        message: close
          ? "No new journals can be posted to a closed period."
          : "The period will accept new journal entries again.",
        confirmLabel: close ? "Close period" : "Reopen period",
        tone: close ? "danger" : "primary",
        successMessage: close ? "Period closed." : "Period reopened.",
        onConfirm: async () => {
          if (close) await closeFinancialPeriod(row.id);
          else await reopenFinancialPeriod(row.id);
          await loadRef.current();
        },
      });
    },
    [confirm, loadRef]
  );

  const columns = useMemo(
    () => [
      { name: "Name", selector: (r) => r.name, sortable: true, grow: 2 },
      {
        name: "Start",
        selector: (r) => r.period_start,
        cell: (r) => formatDate(r.period_start),
        width: "120px",
      },
      {
        name: "End",
        selector: (r) => r.period_end,
        cell: (r) => formatDate(r.period_end),
        width: "120px",
      },
      {
        name: "Branch",
        selector: (r) => r.branch_name || "All branches",
        width: "140px",
      },
      {
        name: "Status",
        cell: (r) => {
          const meta = PERIOD_STATUS_META[r.status] || { label: r.status, color: "secondary" };
          return <Badge color={meta.color}>{meta.label}</Badge>;
        },
        width: "100px",
      },
      {
        name: "Actions",
        cell: (r) => (
          <AccountingIconActions
            actions={[
              r.status === "open"
                ? {
                    icon: "clear",
                    label: "Close period",
                    onClick: () => togglePeriod(r, true),
                    variant: "danger",
                  }
                : {
                    icon: "plus",
                    label: "Reopen period",
                    onClick: () => togglePeriod(r, false),
                    variant: "primary",
                  },
            ]}
          />
        ),
        width: "56px",
        ignoreRowClick: true,
      },
    ],
    [togglePeriod]
  );

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await runCrud(
        () =>
          createFinancialPeriod({
            branch_id: branchId || null,
            name: form.name,
            period_start: form.period_start,
            period_end: form.period_end,
          }),
        { successMessage: "Financial period created." }
      );
      setModalOpen(false);
      setForm({ name: "", period_start: todayIsoDate().slice(0, 7) + "-01", period_end: todayIsoDate() });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle={ACCOUNTING_SECTION} parent={ACCOUNTING_SECTION} title="Financial Periods" />
      <div className="container-fluid">
        {error && <Alert color="danger">{error}</Alert>}
        <div className="card">
          <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
            <h5 className="mb-0">Period register</h5>
            <div className="d-flex flex-wrap gap-2 align-items-center">
              <Input
                type="select"
                bsSize="sm"
                style={{ width: "180px" }}
                value={branchId}
                onChange={(e) => setBranchId(e.target.value)}>
                <option value="">All branches</option>
                {branches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.name}
                  </option>
                ))}
              </Input>
              <Button color="primary" size="sm" onClick={() => setModalOpen(true)}>
                New period
              </Button>
            </div>
          </div>
          <div className="card-body p-0">
            {loading ? (
              <div className="text-center py-5">
                <Spinner color="primary" />
              </div>
            ) : (
              <DataTable columns={columns} data={periods} customStyles={DEFAULT_TABLE_CUSTOM_STYLES} pagination />
            )}
          </div>
        </div>

        <Modal isOpen={modalOpen} toggle={() => setModalOpen(false)}>
          <Form onSubmit={handleCreate}>
            <ModalHeader toggle={() => setModalOpen(false)}>New financial period</ModalHeader>
            <ModalBody>
              <FormGroup>
                <Label>Name</Label>
                <Input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. Jan 2026"
                />
              </FormGroup>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label>Start date</Label>
                    <Input
                      type="date"
                      required
                      value={form.period_start}
                      onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label>End date</Label>
                    <Input
                      type="date"
                      required
                      value={form.period_end}
                      onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                    />
                  </FormGroup>
                </Col>
              </Row>
            </ModalBody>
            <ModalFooter>
              <Button color="secondary" type="button" onClick={() => setModalOpen(false)}>
                Cancel
              </Button>
              <Button color="primary" type="submit" disabled={saving}>
                {saving ? "Creating…" : "Create"}
              </Button>
            </ModalFooter>
          </Form>
        </Modal>
      </div>
    </Fragment>
  );
};

export default FinancialPeriodsPage;
